import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';
import { Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RequestHotshot = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    date_time_required: '',
    pickup_address: '',
    pickup_contact_name: '',
    pickup_contact_phone: '',
    delivery_address: '',
    delivery_contact_name: '',
    delivery_contact_phone: '',
    load_description: '',
    weight: '',
    dimensions: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ['name', 'email', 'phone', 'date_time_required', 'pickup_address', 'pickup_contact_name', 'pickup_contact_phone', 'delivery_address', 'delivery_contact_name', 'delivery_contact_phone', 'load_description', 'weight', 'dimensions'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/hotshot-requests`, formData);
      setSubmitted(true);
      toast.success('Hotshot request submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
          <Card className="max-w-md w-full mx-4 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Request Submitted</h2>
            <p className="text-gray-600 mb-4">Your hotshot request has been received. Our team will be in touch shortly.</p>
            <p className="text-gray-600 mb-6">Need it faster? Call us directly:</p>
            <a href="tel:1300380700" className="inline-flex items-center gap-2 text-xl font-bold text-red-600 hover:text-red-700">
              <Phone className="w-5 h-5" /> 1300 380 700
            </a>
            <div className="mt-6">
              <Button onClick={() => navigate('/')} className="bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white">Back to Home</Button>
            </div>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          {/* Hotshot Hero */}
          <div className="bg-red-600 text-white rounded-xl p-6 mb-8 text-center">
            <h1 className="text-3xl font-bold mb-2">HOTSHOTS</h1>
            <p className="text-lg mb-4">Urgent delivery? We've got you covered.</p>
            <a href="tel:1300380700" className="inline-flex items-center gap-2 bg-white text-red-600 font-bold px-6 py-3 rounded-lg text-lg hover:bg-red-50 transition-colors">
              <Phone className="w-5 h-5" /> Call 1300 380 700
            </a>
            <p className="text-red-100 text-sm mt-3">Or fill out the form below and we'll get back to you ASAP</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Your Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Your Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input name="name" value={formData.name} onChange={handleChange} required placeholder="Full name" data-testid="hotshot-name" />
                </div>
                <div>
                  <Label>Company Name</Label>
                  <Input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Company (optional)" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" data-testid="hotshot-email" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="hotshot-phone" />
                </div>
              </div>
            </Card>

            {/* Job Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Job Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Date & Time Required *</Label>
                  <Input name="date_time_required" type="datetime-local" value={formData.date_time_required} onChange={handleChange} required data-testid="hotshot-datetime" />
                </div>
              </div>
            </Card>

            {/* Pickup */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Pick Up</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Pick Up Address *</Label>
                  <Input name="pickup_address" value={formData.pickup_address} onChange={handleChange} required placeholder="Full pickup address" data-testid="hotshot-pickup-address" />
                </div>
                <div>
                  <Label>Contact Name at Pick Up *</Label>
                  <Input name="pickup_contact_name" value={formData.pickup_contact_name} onChange={handleChange} required placeholder="Contact person" data-testid="hotshot-pickup-contact" />
                </div>
                <div>
                  <Label>Contact Phone at Pick Up *</Label>
                  <Input name="pickup_contact_phone" value={formData.pickup_contact_phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="hotshot-pickup-phone" />
                </div>
              </div>
            </Card>

            {/* Delivery */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Delivery</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Delivery Address *</Label>
                  <Input name="delivery_address" value={formData.delivery_address} onChange={handleChange} required placeholder="Full delivery address" data-testid="hotshot-delivery-address" />
                </div>
                <div>
                  <Label>Contact Name at Delivery *</Label>
                  <Input name="delivery_contact_name" value={formData.delivery_contact_name} onChange={handleChange} required placeholder="Contact person" data-testid="hotshot-delivery-contact" />
                </div>
                <div>
                  <Label>Contact Phone at Delivery *</Label>
                  <Input name="delivery_contact_phone" value={formData.delivery_contact_phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="hotshot-delivery-phone" />
                </div>
              </div>
            </Card>

            {/* Load */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Load Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Load Description *</Label>
                  <Textarea name="load_description" value={formData.load_description} onChange={handleChange} required placeholder="Describe the load..." rows={3} data-testid="hotshot-load-desc" />
                </div>
                <div>
                  <Label>Weight *</Label>
                  <Input name="weight" value={formData.weight} onChange={handleChange} required placeholder="e.g. 500kg" data-testid="hotshot-weight" />
                </div>
                <div>
                  <Label>Dimensions *</Label>
                  <Input name="dimensions" value={formData.dimensions} onChange={handleChange} required placeholder="e.g. 2m x 1m x 1m" data-testid="hotshot-dimensions" />
                </div>
              </div>
            </Card>

            {/* Message */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Additional Information</h2>
              <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Any special requirements or notes..." rows={3} />
            </Card>

            <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6 font-bold" data-testid="submit-hotshot">
              {loading ? 'Submitting...' : 'Submit Hotshot Request'}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RequestHotshot;
